import { AsyncPipe, DatePipe, Location, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator } from '@angular/material/paginator';
import { Router, RouterLink } from '@angular/router';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { map, Observable, of, switchMap } from 'rxjs';
import { BottomBarComponent } from 'src/app/_shared/components/bottom-bar/bottom-bar.component';
import { CardCarouselComponent } from 'src/app/_shared/components/card-carousel/card-carousel.component';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { SearchGalleryComponent } from 'src/app/_shared/components/search-gallery/search-gallery.component';
import { UserService } from 'src/app/common/user/user.service';
import { environment } from 'src/environments/environment';
import { Category, Hashtag, SharryContent, SharrySelection } from 'src/types/graphql';
import { dateIsInCurrentYear, datesAreOnSameDay } from 'utils/date-utils';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { VariantDatePipe } from '../../common/variant/variant-date/variant-date.pipe';
import { HashtagsRankingGQL, HomepageCategoriesGQL, LatestPublicContentsGQL } from './explore.query.generated';
import { ItalyMapComponent } from './italy-map/italy-map.component';
import { RouteTranslatePipe } from '../../_shared/pipes/route-translate.pipe';
import { SlugPipe } from '../../_shared/pipes/slug.pipe';
import { HtmlCurrencyPipe } from '../../_shared/pipes/html-currency.pipe';
import { ContentTypeColorizerPipe } from '../../_shared/pipes/content-type-colorizer.pipe';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { IconComponent } from '../../common/icon/icon.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import {
  AutocompletePlacesFragment,
  AutocompletePlacesGQL,
} from '../../_shared/components/prefix-search/prefix-search.query.generated';
import { RouteTranslateService } from '../../common/route-translate/route-translate.service';
import { municipalities, placesKeys, placesShort, regions } from '../../../../utils/places-utility';
import { BasePlusComponent } from '../../_shared/components/base/base-plus.component';
import { LocationSearchService } from '../../common/location-search/location-search.service';

@Component({
  selector: 'sharry-explore',
  standalone: true,
  templateUrl: './explore.component.html',
  styleUrls: ['./explore.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    AsyncPipe,
    RouterLink,
    I18NextModule,
    RouteTranslatePipe,
    MatButtonModule,
    EllipsisTooltipDirective,
    CardCarouselComponent,
    SlugPipe,
    ContentTypeColorizerPipe,
    HtmlCurrencyPipe,
    ItalyMapComponent,
    BottomBarComponent,
    HeaderComponent,
    VariantDatePipe,
    FooterComponent,
    SearchGalleryComponent,
    MatFormField,
    IconComponent,
    FormsModule,
    MatInput,
    MatAutocomplete,
    MatOption,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatSuffix,
    NgOptimizedImage,
  ],
})
export class ExploreComponent extends BasePlusComponent implements OnInit {
  @ViewChild('autoCompleteInput', { read: MatAutocompleteTrigger })
  autoComplete: MatAutocompleteTrigger;
  @ViewChild('paginatorTemplate', { read: MatPaginator }) paginator: MatPaginator;
  @ViewChild('header') header: HeaderComponent;
  public env = environment.env;
  whereValue = new FormControl('');
  options = signal<AutocompletePlacesFragment[]>([]);
  public homepageCategories$: Observable<Category[]>;
  public latestPublicContents$: Observable<Partial<SharryContent>[]>;

  dateIsInCurrentYear = dateIsInCurrentYear;
  datesAreOnSameDay = datesAreOnSameDay;

  public sharrySelection = SharrySelection;
  public hashtags$: Observable<Hashtag[]> = of([]);
  invalidImages: number[] = [];
  heroImages = [
    {
      ['480']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/1-480.webp`,
      ['768']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/1-768.webp`,
      ['1024']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/1-1024.webp`,
      ['1440']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/1-1440.webp`,
      ['1920']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/1-1920.webp`,
      title$: this.userService?.user$.pipe(map(user => (user ? 'explore.hero--logged' : 'explore.hero--not-logged'))),
      subtitle: 'explore.hero-subtitle-1',
      textColor: '#fff',
    },
    {
      ['480']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/2-480.webp`,
      ['768']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/2-768.webp`,
      ['1024']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/2-1024.webp`,
      ['1440']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/2-1440.webp`,
      ['1920']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/2-1920.webp`,
      title$: this.userService?.user$.pipe(map(user => (user ? 'explore.hero--logged' : 'explore.hero--not-logged'))),
      subtitle: 'explore.hero-subtitle-2',
      textColor: '#fff',
    },
    {
      ['480']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/3-480.webp`,
      ['768']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/3-768.webp`,
      ['1024']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/3-1024.webp`,
      ['1440']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/3-1440.webp`,
      ['1920']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/explore/3-1920.webp`,
      title$: this.userService?.user$.pipe(map(user => (user ? 'explore.hero--logged' : 'explore.hero--not-logged'))),
      subtitle: 'explore.hero-subtitle-3',
      textColor: '#fff',
    },
  ];
  squareBoxes = [
    {
      id: 1,
      title: 'explore.square-button.box-1.title',
      link: 'community',
      button: false,
      scroll: '',
    },
    {
      id: 2,
      title: 'explore.square-button.box-2.title',
      link: '',
      button: true,
      scroll: '',
    },
    {
      id: 3,
      title: 'explore.square-button.box-3.title',
      link: 'cherry-meter',
      button: false,
      scroll: '',
    },
    {
      id: 4,
      title: 'explore.square-button.box-4.title',
      link: 'profile/home',
      button: false,
      scroll: 'invite',
    },
    {
      id: 5,
      title: 'explore.square-button.box-5.title',
      link: 'wonders-map',
      button: false,
      scroll: '',
    },
  ];

  constructor(
    @Inject(I18NEXT_SERVICE) public translateService: ITranslationService,
    public userService: UserService,
    private homepageCategoriesGQL: HomepageCategoriesGQL,
    private latestPublicContentsGQL: LatestPublicContentsGQL,
    private hashtagsRankingGQL: HashtagsRankingGQL,
    private location: Location,
    private autocompletePlacesGQL: AutocompletePlacesGQL,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
    private locationSearchService: LocationSearchService,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
  ) {
    super();
    this.metadataService.setMetadataPage('explore', this.location.path());
    this.homepageCategories$ = this.homepageCategoriesGQL
      .fetch({}, { fetchPolicy: 'no-cache' })
      .pipe(map(r => r.data.allCategories as Category[]));
    this.latestPublicContents$ = this.latestPublicContentsGQL
      .fetch({ limit: 12 }, { fetchPolicy: 'no-cache' })
      .pipe(map(r => r.data.latestPublicContents as any as SharryContent[]));
    this.hashtags$ = this.hashtagsRankingGQL.fetch({}).pipe(map(r => r.data.hashtagSearch as Hashtag[]));
    this.whereValue.valueChanges
      .pipe(
        switchMap((place: string) => {
          if (place) {
            return this.autocompletePlacesGQL
              .fetch({
                text: place,
                limit: 5,
              })
              .pipe(map(response => response.data.results));
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((result: AutocompletePlacesFragment[]) => {
        this.options.set(result);
      });
  }

  ngOnInit(): void {
    window.addEventListener('scroll', this.scrollEvent, true);
    window.addEventListener('focusout', this.handleFocusOut);
  }

  scrollEvent = (): void => {
    if (this.autoComplete.panelOpen) {
      this.autoComplete.updatePosition();
    }
  };

  handleFocusOut = (): void => {
    setTimeout(() => {
      if (this.autoComplete.panelOpen) {
        this.autoComplete.updatePosition();
      }
    }, 100);
  };

  onOpenSearchModal() {
    this.header.onOpenModalSearch();
  }

  onSearch(event: any): void {
    if (event.source.value) {
      const placeFragment = this.options().find(place => place.title === event.source.value);
      this.navigateToResults(placeFragment);
    }
  }

  onSearchByButton() {
    if (this.whereValue.value) {
      const placeFragment = this.options().find(place => place.title === this.whereValue.value);
      if (placeFragment) {
        this.navigateToResults(placeFragment);
      }
    }
  }

  navigateToResults(option: AutocompletePlacesFragment): void {
    const { isRegion, isProvince, isMunicipality, latitude, longitude, title } = option;
    const queryParams = {
      value: title,
      prefix: 'location',
      distance: !(isRegion || isProvince) ? 50 : undefined,
      latitude,
      longitude,
      region: isRegion ? Object.keys(regions).find(key => regions[key] === title) : undefined,
      province: isProvince
        ? placesKeys[Object.keys(placesShort).find(key => placesShort[key] === title.replace(', Italia', '')) as 'BT']
        : undefined,
      municipality: isMunicipality
        ? Object.keys(municipalities).find(key => municipalities[key] === this.whereValue.value)
        : undefined,
      city: !(isRegion || isProvince || isMunicipality) ? this.whereValue.value.replace(', Italia', '') : undefined,
    };
    this.router.navigate([this.routeTranslateService.translateRoute('search-results')], { queryParams });
  }

  onRemoveDate() {
    this.whereValue.setValue(undefined);
  }

  onLocationSearch() {
    this.locationSearchService.onSearch();
  }
}
